import {
    Grid,
    SimpleCentered,
    SimpleLeftAligned,
    SplitWithLogosOnRight,
} from "@/views/blocks/logos/logos";
import type {
    GridProps,
    SimpleCenteredProps,
    SimpleLeftAlignedProps,
    SplitWithLogosOnRightProps,
} from "@/views/blocks/logos/logos";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { BlockDef } from "./block.interfaces";
import {
    arrayField,
    createBlock,
    headingField,
    textField,
    buttonField,
    linkTextField,
} from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import { LOGO_API_BASE_URL } from "../page.service";
import { useFieldConfig } from "./useFieldConfig";

export function useLogos() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const simpleCenteredBlock = computed<BlockDef>(() =>
        createBlock<SimpleCenteredProps>({
            typeId: "SimpleCentered",
            type: "logos",
            name: t("page.blocks.logos.simpleCentered"),
            component: SimpleCentered,
        })
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Trusted by industry leaders in data science",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withArrayField(
                "logos",
                arrayField(
                    translations.value.logo,
                    "logos",
                    {
                        src: { label: translations.value.src, type: "image" },
                        alt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        {
                            src: `${LOGO_API_BASE_URL}/1`,
                            alt: "Alt 1",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/2`,
                            alt: "Alt 2",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/3`,
                            alt: "Alt 3",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/4`,
                            alt: "Alt 4",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/5`,
                            alt: "Alt 5",
                        },
                    ],
                    {
                        src: `${LOGO_API_BASE_URL}/6`,
                        alt: "Alt N",
                    }
                )
            )
            .withField(
                "callToAction",
                linkTextField(
                    t("page.block.fields.callToAction"),
                    {
                        text: "Over 500 organizations trust our data science curriculum and graduates.",
                        label: "See success stories",
                        href: "#",
                    },
                    {
                        text: translations.value.text,
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const gridBlock = computed<BlockDef>(() =>
        createBlock<GridProps>({
            typeId: "Grid",
            type: "logos",
            name: t("page.blocks.logos.grid"),
            component: Grid,
        })
            .withArrayField(
                "logos",
                arrayField(
                    translations.value.logo,
                    "logos",
                    {
                        src: { label: translations.value.src, type: "image" },
                        alt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        {
                            src: `${LOGO_API_BASE_URL}/1`,
                            alt: "Alt 1",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/2`,
                            alt: "Alt 2",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/3`,
                            alt: "Alt 3",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/4`,
                            alt: "Alt 4",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/5`,
                            alt: "Alt 5",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/6`,
                            alt: "Alt 6",
                        },
                    ],
                    {
                        src: `${LOGO_API_BASE_URL}/7`,
                        alt: "Alt N",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const simpleLeftAlignedBlock = computed<BlockDef>(() =>
        createBlock<SimpleLeftAlignedProps>({
            typeId: "SimpleLeftAligned",
            type: "logos",
            name: t("page.blocks.logos.simpleLeftAligned"),
            component: SimpleLeftAligned,
        })
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Partnering with industry-leading organizations",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withArrayField(
                "logos",
                arrayField(
                    translations.value.logo,
                    "logos",
                    {
                        src: { label: translations.value.src, type: "image" },
                        alt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        {
                            src: `${LOGO_API_BASE_URL}/1`,
                            alt: "Alt 1",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/2`,
                            alt: "Alt 2",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/3`,
                            alt: "Alt 3",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/4`,
                            alt: "Alt 4",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/5`,
                            alt: "Alt 5",
                        },
                    ],
                    {
                        src: `${LOGO_API_BASE_URL}/6`,
                        alt: "Alt N",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const splitWithLogosOnRightBlock = computed<BlockDef>(() =>
        createBlock<SplitWithLogosOnRightProps>({
            typeId: "SplitWithLogosOnRight",
            type: "logos",
            name: t("page.blocks.logos.splitWithLogosOnRight"),
            component: SplitWithLogosOnRight,
        })
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Trusted by leading industry partners",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                textField(
                    translations.value.description,
                    "Our platform is trusted by innovative companies to streamline workflows and enhance productivity. Join these forward-thinking organizations today.",
                    translations.value.text
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "View case studies",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withArrayField(
                "logos",
                arrayField(
                    translations.value.logo,
                    "logos",
                    {
                        src: { label: translations.value.src, type: "image" },
                        alt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        {
                            src: `${LOGO_API_BASE_URL}/1`,
                            alt: "Alt 1",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/2`,
                            alt: "Alt 2",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/3`,
                            alt: "Alt 3",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/4`,
                            alt: "Alt 4",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/5`,
                            alt: "Alt 5",
                        },
                        {
                            src: `${LOGO_API_BASE_URL}/6`,
                            alt: "Alt 6",
                        },
                    ],
                    {
                        src: `${LOGO_API_BASE_URL}/7`,
                        alt: "Alt N",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            simpleCenteredBlock.value,
            gridBlock.value,
            simpleLeftAlignedBlock.value,
            splitWithLogosOnRightBlock.value,
        ]),
    };
}
